import React, { useEffect, useState } from 'react';
import { useUrlLocalizer, useTranslator } from 'tradera/lang/translate';
import { useInView } from 'react-intersection-observer';

import { trackContainerClick } from '../analytics';
import { Button } from 'tradera/components/atoms/button/button';
import { myTraderaUrls } from 'tradera/apps/my-tradera/app/pages/routes';
import { Carousel } from 'tradera/components/carousel/carousel';
import {
    LeftButton,
    RightButton
} from 'tradera/components/carousel/carousel-buttons';
import { CarouselContent } from 'tradera/components/carousel/carousel-content';
import type { PossibleItemTypes } from 'tradera/components/item-card/item-card';
import { ItemCard } from 'tradera/components/item-card/item-card';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import {
    type ItemDtoPlaceHolder,
    generateItemCardPlaceholders
} from 'tradera/components/item-card/generate-item-card-placeholders';
import * as itemActions from 'tradera/state/items/actions';
import uiActions from 'tradera/state/ui/actions';
import { MoreResultsCard } from 'tradera/components/carousel/more-results-card/more-results-card';
import { selectMemberId } from 'tradera/state/member/selectors';
import { FallbackBanner } from '../fallback-banner/fallback-banner';
import AlertIcon from '@tradera/blueprint/theme/icons2/alert.svg?jsx';
import { useGetSavedSearches } from './use-get-saved-searches';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import { StartPageSection } from '../start-page-section/start-page-section';
import { StartPageSectionTitle } from '../start-page-section/start-page-section-title';
import { isServer } from 'tradera/utils/nextjs';
import ArrowRight from '@tradera/blueprint/theme/icons2/arrow-right.svg?jsx';

export const SavedSearches = () => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const isMobile = useIsMobile();
    const dispatch = useAppDispatch();
    const memberId = useAppSelector(selectMemberId);
    const [fallbackItems, setFallbackItems] = useState<ItemDtoPlaceHolder[]>(
        []
    );

    const [loadingTriggerRef, loadingTriggerInView] = useInView({
        triggerOnce: true
    });
    const [selectedSearchIndex, setSelectedSearchIndex] = React.useState(0);
    const {
        items,
        itemsIsLoading,
        itemsError,
        savedSearchesCount,
        savedSearchName,
        savedSearchId,
        savedSearches,
        totalItems
    } = useGetSavedSearches(loadingTriggerInView, selectedSearchIndex);
    const selectedSavedSearchUrl = toLocalizedUrl(
        `/search/named/${memberId}/${savedSearchId}/${savedSearchName}`
    );

    // To indicate that the list is scrollable on mobile we show 2.5 items
    const numberCarouselCards = isMobile ? 2.5 : 6;
    const loadingItemsCount = isMobile ? 2 : 6;
    const noResultsText = isServer
        ? ''
        : t('startPage_savedSearches_noResults');

    useEffect(() => {
        // Translations may not be loaded at first render on client
        // so we need to update the fallback items when translations are loaded.
        setFallbackItems(
            generateItemCardPlaceholders(loadingItemsCount, noResultsText)
        );
    }, [loadingItemsCount, noResultsText]);

    const displayItems = itemsIsLoading
        ? generateItemCardPlaceholders(
              // Should have same number of loading cards as the last number of items we showed.
              // If no items were found, then we use `loadingItemsCount`.
              items.length === 0
                  ? loadingItemsCount
                  : Math.min(items.length, loadingItemsCount)
          )
        : items.length === 0
        ? fallbackItems
        : items;

    const openQuickView = (item: PossibleItemTypes) => {
        dispatch(
            uiActions.openQuickViewAction({
                items,
                item,
                openedFrom: 'Start Page Saved Searches'
            })
        );
    };

    const toggleWishList = (itemId: number) => {
        dispatch(itemActions.wishlist(itemId));
    };

    const handleSavedSearchClick = (index: number) => {
        if (index === selectedSearchIndex) {
            trackContainerClick(
                'savedSearches',
                'Saved-Searches click chip search'
            );
            return;
        }

        trackContainerClick('savedSearches', 'Saved-Searches click chip');
        setSelectedSearchIndex(index);
    };

    const moreItemsCanBeFetched = totalItems > items.length;
    const savedSearchesExists = savedSearchesCount > 0;
    const isWaitingForSavedSearches =
        savedSearchesExists && savedSearches.length === 0;
    const isServerWithSavedSearches = isServer && savedSearchesExists;

    // On server we show a placeholder because the sorting of the saved searches is done client side.
    // On client we show a placeholder if we are waiting for the sorted saved searches to be fetched.
    const savedSearchesOrPlaceholders =
        isWaitingForSavedSearches || isServerWithSavedSearches
            ? [{ id: 0, name: '...' }]
            : savedSearches;

    return (
        <StartPageSection ref={loadingTriggerRef}>
            {savedSearchesExists && (
                <>
                    <StartPageSectionTitle
                        href={toLocalizedUrl(myTraderaUrls.savedSearches)}
                        inSpa={false} // Use 'inSPA' when mytradera-page can be navigated to without a hard reload
                        onClick={() =>
                            trackContainerClick(
                                'savedSearches',
                                'Saved-Searches button below'
                            )
                        }
                        showAll>
                        {t('myt_savedSearches_title')}
                    </StartPageSectionTitle>
                    <Carousel
                        id="saved-searches-carousel-1"
                        gap="0.5rem"
                        numberItemsToScroll={5}
                        className="mx-md-3 mx-lg-2 mx-xl-0"
                        padX>
                        <LeftButton light />
                        <CarouselContent>
                            {savedSearchesOrPlaceholders.map((item, index) => {
                                const isSelected =
                                    index === selectedSearchIndex;
                                return (
                                    <Button
                                        theme="action"
                                        variant={
                                            isSelected ? 'filled' : 'outline'
                                        }
                                        href={
                                            isSelected && selectedSavedSearchUrl
                                        }
                                        iconRight={
                                            isSelected && (
                                                <ArrowRight className="icon icon-xs" />
                                            )
                                        }
                                        key={item.id}
                                        onClick={() =>
                                            handleSavedSearchClick(index)
                                        }>
                                        {item.name}
                                    </Button>
                                );
                            })}
                        </CarouselContent>
                        <RightButton light />
                    </Carousel>
                    {!!itemsError && (
                        <FallbackBanner
                            className="mx-2 mx-md-3 mx-lg-2 mx-xl-0 mt-2"
                            icon={
                                <AlertIcon className="icon icon-xxl icon-gray-500" />
                            }>
                            <>
                                <p>{t('error_somethingWentWrong')}</p>
                                <p className="m-0">
                                    <Button
                                        theme="link"
                                        href={selectedSavedSearchUrl}
                                        onClick={() =>
                                            trackContainerClick(
                                                'savedSearches',
                                                'Saved searches failed to load button'
                                            )
                                        }>
                                        {t(
                                            'startPage_savedSearches_failedToLoadSearchLink'
                                        )}
                                    </Button>
                                </p>
                            </>
                        </FallbackBanner>
                    )}
                    {displayItems?.length > 0 && (
                        <Carousel
                            id="saved-searches-carousel-2"
                            numberItemsToShow={numberCarouselCards}
                            numberItemsToScroll={numberCarouselCards}
                            className="mx-md-3 mx-lg-2 mx-xl-0 mt-2"
                            padX>
                            <LeftButton alignVerticallyToItemCards size="md" />
                            <CarouselContent>
                                {displayItems.map((item) => (
                                    <ItemCard
                                        // This is to make the parent container set grid gaps instead
                                        // of individual cards keeping paddings and margins set of their own.
                                        // Makes displaying cards in different situations more predictable.
                                        className="p-0 m-0"
                                        item={item}
                                        key={item.itemId}
                                        onAddToWishlist={toggleWishList}
                                        onRemoveFromWishlist={toggleWishList}
                                        onQuickView={openQuickView}
                                        showFallback={
                                            !itemsIsLoading && !item.itemUrl
                                        }
                                        gaCategory="Startpage saved searches"
                                        showBidStatus
                                        showTitleOnOneLine
                                        showShippingOrOnDisplay={false}
                                    />
                                ))}
                                {moreItemsCanBeFetched ? (
                                    <MoreResultsCard
                                        href={selectedSavedSearchUrl}
                                        onClick={() =>
                                            trackContainerClick(
                                                'savedSearches',
                                                'Saved searches see all button'
                                            )
                                        }
                                        label={t(
                                            'startPage_savedSearches_moreResults'
                                        )}
                                    />
                                ) : null}
                            </CarouselContent>
                            <RightButton alignVerticallyToItemCards size="md" />
                        </Carousel>
                    )}
                </>
            )}
        </StartPageSection>
    );
};
